<script setup>
// O formulário de SMS, dentro da gaveta (título no cabeçalho, Criar na gaveta). Duas seções que abrem e fecham
// (8-6 = A). Validação, `prepareCampaignDetails` e o evento `submit` são os do woot.
import { reactive, computed, ref, watch, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import MultiSelectInput from 'dashboard/components-next/polaris/cards/MultiSelectInput.vue';
import SearchSelect from 'dashboard/components-next/polaris/cards/SearchSelect.vue';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';

const emit = defineEmits(['submit']);
const title = defineModel('title', { type: String, default: '' });

const MESSAGE_MAX_LENGTH = 200;

const { t } = useI18n();
const uid = useId();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getSMSInboxes'),
};

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });
const baseline = ref(JSON.stringify(initialState));

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isSaving = computed(() => formState.uiFlags.value.isCreating);
const isSubmitDisabled = computed(() => v$.value.$invalid);
const isDirty = computed(() => JSON.stringify(state) !== baseline.value);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const formErrors = computed(() => ({
  title: v$.value.title.$error ? t('CAMPAIGN.SMS.CREATE.FORM.TITLE.ERROR') : '',
  message: v$.value.message.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.ERROR')
    : '',
  inbox: v$.value.inboxId.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.INBOX.ERROR')
    : '',
  scheduledAt: v$.value.scheduledAt.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.ERROR')
    : '',
  audience: v$.value.selectedAudience.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.ERROR')
    : '',
}));

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  baseline.value = JSON.stringify(initialState);
  v$.value.$reset();
};

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: state.selectedAudience?.map(id => ({
    id,
    type: 'Label',
  })),
});

// Valida e entrega os dados; o Criar da gaveta chama isto. Devolve se havia o que entregar.
const submit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return false;

  emit('submit', prepareCampaignDetails());
  return true;
};

// O título é o campo do cabeçalho da gaveta: dois sentidos, sem laço.
watch(
  title,
  value => {
    if (value !== state.title) state.title = value;
  },
  { immediate: true }
);
watch(
  () => state.title,
  value => {
    if (value !== title.value) title.value = value;
  }
);

defineExpose({
  prepareCampaignDetails,
  isSubmitDisabled,
  isDirty,
  isSaving,
  submit,
  resetState,
  state,
});
</script>

<template>
  <form
    class="grid gap-6"
    novalidate
    data-campaign-form="sms"
    @submit.prevent="submit"
  >
    <FieldError
      v-if="formErrors.title"
      :errors="[formErrors.title]"
      data-campaign-title-error
    />

    <FoldingGroup :title="t('CAMPAIGN.DRAWER.SECTIONS.MESSAGE')">
      <FieldGroup class="gap-4">
        <Field :data-invalid="!!formErrors.message">
          <FieldLabel :for="`${uid}-message`">
            {{ t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.LABEL') }}
          </FieldLabel>
          <Textarea
            :id="`${uid}-message`"
            v-model="state.message"
            :maxlength="MESSAGE_MAX_LENGTH"
            :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.PLACEHOLDER')"
            :aria-invalid="!!formErrors.message"
            @blur="v$.message.$touch()"
          />
          <p
            class="type-polaris-caption tabular-nums text-polaris-muted-foreground ms-auto"
          >
            {{
              t('CAMPAIGN.DRAWER.CHARACTERS', {
                count: state.message.length,
                max: MESSAGE_MAX_LENGTH,
              })
            }}
          </p>
          <FieldError
            v-if="formErrors.message"
            :errors="[formErrors.message]"
          />
        </Field>

        <Field :data-invalid="!!formErrors.inbox">
          <FieldLabel :for="`${uid}-inbox`">
            {{ t('CAMPAIGN.SMS.CREATE.FORM.INBOX.LABEL') }}
          </FieldLabel>
          <SearchSelect
            :id="`${uid}-inbox`"
            v-model="state.inboxId"
            :options="inboxOptions"
            :label="t('CAMPAIGN.SMS.CREATE.FORM.INBOX.LABEL')"
            :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.INBOX.PLACEHOLDER')"
            :aria-invalid="!!formErrors.inbox"
          />
          <FieldError v-if="formErrors.inbox" :errors="[formErrors.inbox]" />
        </Field>

        <Field :data-invalid="!!formErrors.audience">
          <FieldLabel :for="`${uid}-audience`">
            {{ t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL') }}
          </FieldLabel>
          <MultiSelectInput
            :id="`${uid}-audience`"
            v-model="state.selectedAudience"
            :options="audienceList"
            :label="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.LABEL')"
            :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
            :aria-invalid="!!formErrors.audience"
          />
          <FieldError
            v-if="formErrors.audience"
            :errors="[formErrors.audience]"
          />
        </Field>
      </FieldGroup>
    </FoldingGroup>

    <FoldingGroup :title="t('CAMPAIGN.DRAWER.SECTIONS.SEND')">
      <FieldGroup class="gap-4">
        <Field :data-invalid="!!formErrors.scheduledAt">
          <FieldLabel :for="`${uid}-scheduled`">
            {{ t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.LABEL') }}
          </FieldLabel>
          <Input
            :id="`${uid}-scheduled`"
            v-model="state.scheduledAt"
            type="datetime-local"
            :min="currentDateTime"
            :placeholder="
              t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.scheduledAt"
            @blur="v$.scheduledAt.$touch()"
          />
          <FieldError
            v-if="formErrors.scheduledAt"
            :errors="[formErrors.scheduledAt]"
          />
        </Field>
      </FieldGroup>
    </FoldingGroup>
  </form>
</template>
